/**
 * Description : Barre de navigation principale avec sélecteur de projet, liens (Accueil / Création / Suivi / Incidents), notifications et menu utilisateur. Inclut un menu hamburger en mobile.
 * Utilisé dans : app/layout.tsx, app/layout.client.tsx
 */
'use client'
import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Menu as MenuIcon, X } from 'lucide-react'
import { useProject } from '@/contexts/ProjectContext';
import { useSession } from 'next-auth/react'
import ProjectSelector from '@/components/molecules/ProjectSelector'
import UserMenu from '../molecules/UserMenu'
import ProjectCreationModal from '../molecules/ProjectCreationModal'
import MobileNav from '../molecules/MobileNav'
import { usePendingMoyens } from '@/contexts/PendingMoyensContext'

async function fetchProjects() {
  const res = await fetch('/api/projets');
  if (!res.ok) return [];
  return await res.json();
}

/**
 * Rôle : Affiche la barre de navigation avec sélection de projet, navigation principale et menu utilisateur.
 * Logique :
 * - Charge les projets et permet d'en créer un nouveau via des actions UI.
 * - Adapte l'affichage : liens visibles en desktop, menu hamburger en mobile.
 * Retour : JSX de la barre de navigation.
 */
export default function Navigation() {

  
  const pathname = usePathname()

  const { project: selectedProject, setProject: setSelectedProject, projects, setProjects } = useProject();
  const { pendingCount } = usePendingMoyens();

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: session } = useSession();
  const [pendingMoyensCount, setPendingMoyensCount] = useState(0)

const displayName = (session?.user as any)?.name || '';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  useEffect(() => {
    fetchProjects().then((data) => {
      setProjects(data)
    })
  }, [setProjects])

  // Récupère le nombre de moyens provisoires pour le badge "Création"
useEffect(() => {
  let mounted = true;

  const fetchPendingCount = async () => {
    try {
      const res = await fetch("/api/moyens_provisoires");
      if (!res.ok) return;
      const data = await res.json();

      if (!Array.isArray(data)) return;

      // On ne compte que les moyens incomplets
      const incomplete = data.filter((m: any) => {
        const missingFields = m.missingFields ?? [];
        const isIncomplete =
          missingFields.length > 0 || m.status === "incomplete";
        // Si un projet est sélectionné, on filtre par projetId
        if (selectedProject?.id) {
          return m.projetId === selectedProject.id && isIncomplete;
        }
        return isIncomplete;
      });

      if (mounted) setPendingMoyensCount(incomplete.length);
    } catch (e) {
      console.error("Erreur récupération moyens provisoires :", e);
    }
  };

  fetchPendingCount();
  // Actualisation quand on change de projet
  const id = setInterval(fetchPendingCount, 30000);
  return () => {
    mounted = false;
    clearInterval(id);
  };
}, [selectedProject]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  const handleSelect = (project: { id: number; nom: string }) => {
    setSelectedProject(project)
    setDropdownOpen(false)
  }

  const navItems = [
    { path: '/', label: 'Accueil' },
    { path: '/creer_moyens', label: 'Création' },
    { path: '/suivi_moyens', label: 'Suivi' },
    { path: '/incidents', label: 'Incidents' }, 
  ]

  const isActive = (path: string): boolean => 
    path === '/' ? pathname === '/' : !!pathname && pathname.startsWith(path)
  

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            <div className="md:hidden">
              <button
                type="button"
                aria-label="Ouvrir le menu"
                aria-expanded={mobileMenuOpen}
                className="p-2 rounded-md border border-gray-200 bg-gray-100 hover:bg-gray-200 focus:outline-none"
                onClick={() => setMobileMenuOpen((v) => !v)}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5 text-gray-700" />
                ) : (
                  <MenuIcon className="h-5 w-5 text-gray-700" />
                )}
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <Link href="/" className="block">
                <div className="relative h-6 w-[20px]">
                  <Image
                    src="../Bertrandt_b_schwarz.svg"
                    alt="Logo Bertrandt"
                    fill
                    className="object-contain"
                    priority
                    sizes="20px"
                  />
                </div>
              </Link>
              <ProjectSelector
                projects={projects}
                selectedProject={selectedProject}
                onSelect={handleSelect}
                onCreateClick={() => setShowProjectModal(true)}
              />
            </div>
            <div className="hidden md:flex space-x-6">
              {navItems.map((item) => (
                <Link key={item.path} href={item.path} passHref>
                  <span
                    className={`relative px-1 pb-4 text-sm font-medium border-b-2 hover-neutral cursor-pointer ${
                      isActive(item.path)
                        ? 'text-blue-600 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 border-transparent'
                    }`}
                  >
                    {item.label}
                    {item.label === 'Création' && pendingCount > 0 && (
                      <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                        {pendingCount}
                      </span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <UserMenu initials={initials} displayName={displayName} />
          </div>
        </div>
      </div>
      <MobileNav
        mobileMenuOpen={mobileMenuOpen}
        navItems={navItems}
        isActive={isActive}
        onClose={() => setMobileMenuOpen(false)}
        pendingMoyensCount={pendingMoyensCount}
      />


      <ProjectCreationModal
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        projects={projects}
        onProjectSelect={(p) => {
          setSelectedProject(p)
          setDropdownOpen(false)
          setShowProjectModal(false)
        }}
        onProjectCreate={(created) => {
          setProjects([...projects, created])
          setSelectedProject(created)
          setDropdownOpen(false)
          setShowProjectModal(false)
        }}
      />
    </nav>
  )
}